import { Deserializable } from '../../protocols/deserializable';
import { SectionThemeDefaultColumnKeys } from '../../enum/shared/section-theme-default-column-keys';
import type { SectionColumnConfigKey } from '../../enum/shared/section-column-config-key';
import type { SectionColumnConfigFontStyle } from '../../enum/shared/section-column-config-font-style';
import type { SectionColumnConfigDataValue } from '../../enum/shared/section-column-config-data-value';
import type { SectionColumnConfigState } from '../../enum/shared/section-column-config-state';
import { EnumUtils } from '../../../utils/enum-utils';
import { isProductTagGroupKey } from '../../enum/shared/section-theme-tag-group-keys';

export class SectionColumnConfig implements Deserializable {
  static mergeUserAndThemeColumnConfigs(
    userSpecifiedColumnConfig: Map<SectionColumnConfigKey, SectionColumnConfig>,
    themeSpecifiedColumnConfig: Map<SectionColumnConfigKey, SectionColumnConfig>
  ): Map<SectionColumnConfigKey, SectionColumnConfig> {
    const merged = new Map<SectionColumnConfigKey, SectionColumnConfig>();
    // Iterate over all user specified keys. The API will always return any required keys or those with a value set
    const terpeneKeys = EnumUtils.getTerpenesPascalCased();
    const secCannabinoidKeys = EnumUtils.getSecondaryCannabinoids()?.map(c => c.toString());
    [...userSpecifiedColumnConfig.keys()]?.forEach((key: SectionColumnConfigKey) => {
      let themeKey = key;
      if (terpeneKeys?.includes(key)) {
        themeKey = SectionThemeDefaultColumnKeys.IndividualTerpenes;
      } else if (secCannabinoidKeys?.includes(key)) {
        themeKey = SectionThemeDefaultColumnKeys.SecondaryCannabinoids;
      } else if (isProductTagGroupKey(key)) {
        themeKey = SectionThemeDefaultColumnKeys.TagGroups;
      }
      const user = userSpecifiedColumnConfig?.get(key);
      const theme = themeSpecifiedColumnConfig?.get(themeKey);
      const mergedConfig = SectionColumnConfig.merge(user, theme);
      merged.set(key, mergedConfig);
    });
    return merged;
  }

  static merge(user: SectionColumnConfig, theme: SectionColumnConfig): SectionColumnConfig {
    const result = new SectionColumnConfig();
    result.defaultState = user?.defaultState || theme?.defaultState;
    if (user?.columnName === '') {
      result.columnName = '';
    } else {
      result.columnName = user?.columnName || theme?.columnName || '';
    }
    result.columnWidth = user?.columnWidth || theme?.columnWidth;
    // isFixedLocation is not included on user defaults
    result.isFixedLocation = theme?.isFixedLocation;
    result.dataValue = user?.dataValue || theme?.dataValue;
    // FontStyle, FontColor, ColumnColor, ColumnOpacity are not included on theme defaults, so no fallback value
    result.fontStyle = user?.fontStyle;
    result.fontColor = user?.fontColor;
    result.columnColor = user?.columnColor;
    result.columnOpacity = user?.columnOpacity;
    return result;
  }

  public defaultState: SectionColumnConfigState;
  public columnName: string;
  public columnWidth: number;
  public dataValue: SectionColumnConfigDataValue;
  public fontStyle: SectionColumnConfigFontStyle;
  public fontColor: string = null;
  public columnColor: string = null;
  public columnOpacity: number = null;
  public isFixedLocation: boolean = false;

  onDeserialize() {}
}
